import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";

import { api, get, post } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosPage } from "mobile/components/IosParity";

const parseCoreDocumentId = (raw) => {
  const value = String(raw || "").trim();
  if (!value.startsWith("cr-")) return null;
  return value.slice(3).trim() || null;
};

const uploadFiles = async (files) => {
  const list = Array.from(files || []).filter(Boolean);
  if (!list.length) return [];
  const formData = new FormData();
  list.forEach((file) => formData.append("files", file));
  const res = await api.post("/api/upload", formData);
  const rows = Array.isArray(res?.data) ? res.data : [];
  return rows.map((row) => Number(row?.id || 0)).filter((id) => id > 0);
};

function PhotoThumb({ src, label, onClick, onRemove, removable }) {
  return (
    <Box sx={{ position: "relative", width: 76, height: 76, flex: "0 0 auto" }}>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          border: "1px solid rgba(15,15,18,0.08)",
          borderRadius: 2,
          p: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          bgcolor: "#F7F8FA",
          cursor: "pointer",
        }}
      >
        <Box component="img" src={src} alt={label} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </Box>
      {removable ? (
        <IconButton
          size="small"
          aria-label={`Remove ${label}`}
          onClick={onRemove}
          sx={{
            position: "absolute",
            top: -8,
            right: -8,
            bgcolor: "#A51812",
            color: "#fff",
            width: 22,
            height: 22,
            "&:hover": { bgcolor: "#8F1410" },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      ) : null}
    </Box>
  );
}

PhotoThumb.propTypes = {
  src: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onRemove: PropTypes.func,
  removable: PropTypes.bool,
};

export default function IncomingCoreReceiveMobile() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { documentId: routeDocumentId } = useParams();
  const returnRequestDocumentId = parseCoreDocumentId(routeDocumentId);
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [coreSearch, setCoreSearch] = useState("");
  const [debouncedCoreSearch, setDebouncedCoreSearch] = useState("");
  const [receivedCoreItem, setReceivedCoreItem] = useState(null);
  const [notice, setNotice] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCoreSearch(coreSearch.trim()), 250);
    return () => clearTimeout(timer);
  }, [coreSearch]);

  const detailQuery = useQuery({
    queryKey: ["m-inbound-core", returnRequestDocumentId],
    queryFn: async () => {
      const resp = await get(`/api/core-returns/inbound/${encodeURIComponent(returnRequestDocumentId)}`);
      return resp?.data || null;
    },
    enabled: !!returnRequestDocumentId,
  });

  const row = detailQuery.data;
  const alreadyReceived = Boolean(row?.warehouseInspectedAt);
  const existingPhotos = Array.isArray(row?.warehouseInspectionImages) ? row.warehouseInspectionImages : [];

  const pendingPreviews = useMemo(
    () =>
      files.map((file, index) => ({
        key: `${file.name}-${file.size}-${file.lastModified}-${index}`,
        url: URL.createObjectURL(file),
        label: file.name || `Photo ${index + 1}`,
      })),
    [files],
  );

  useEffect(() => {
    if (row?.warehouseInspectionNotes != null) {
      setNotes(String(row.warehouseInspectionNotes || ""));
    }
    if (row?.receivedCoreItem?.documentId) {
      setReceivedCoreItem({
        documentId: String(row.receivedCoreItem.documentId),
        sku: String(row.receivedCoreItem.sku || ""),
        name: String(row.receivedCoreItem.name || ""),
      });
    }
  }, [row?.documentId, row?.warehouseInspectionNotes, row?.receivedCoreItem?.documentId]);

  const coreItemsQuery = useQuery({
    queryKey: ["m-core-items", debouncedCoreSearch],
    queryFn: async () => {
      const filters = {
        itemType: { $eq: "CORE" },
        ...(debouncedCoreSearch
          ? {
              $or: [
                { sku: { $containsi: debouncedCoreSearch } },
                { name: { $containsi: debouncedCoreSearch } },
              ],
            }
          : {}),
      };
      const resp = await get("/api/inventory-items", {
        filters,
        fields: ["documentId", "sku", "name", "itemType"],
        pagination: { page: 1, pageSize: 20 },
        sort: "sku:asc",
      });
      return Array.isArray(resp?.data) ? resp.data : [];
    },
    enabled: debouncedCoreSearch.length >= 1,
  });

  const coreOptions = useMemo(() => coreItemsQuery.data ?? [], [coreItemsQuery.data]);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []).filter(Boolean);
    if (!list.length) return;
    setFiles((prev) => [...prev, ...list]);
  };

  const receiveMut = useMutation({
    mutationFn: async () => {
      if (!returnRequestDocumentId) throw new Error("Missing return request.");
      if (!receivedCoreItem?.documentId) throw new Error("Select the core part number received.");
      const imageIds = await uploadFiles(files);
      if (!imageIds.length && !alreadyReceived) {
        throw new Error("Add at least one receive photo.");
      }
      return post("/api/core-returns/receive", {
        data: {
          returnRequestDocumentId,
          receivedCoreItemDocumentId: receivedCoreItem.documentId,
          warehouseInspectionImageIds: imageIds,
          notes: notes.trim() || null,
        },
      });
    },
    onSuccess: (result) => {
      setNotice({ type: "success", text: result?.message || "Core received in warehouse." });
      setFiles([]);
      qc.invalidateQueries({ queryKey: ["m-inbound-cores"] });
      detailQuery.refetch();
    },
    onError: (error) =>
      setNotice({
        type: "error",
        text: error?.response?.data?.error?.message || error?.message || "Could not record receive.",
      }),
  });

  if (!returnRequestDocumentId) {
    return (
      <IosPage>
        <MobileTopBar title="Incoming core" onBack={() => navigate("/m/warehouse/receiving?tab=cores")} />
        <Alert severity="error" sx={{ mt: 1 }}>
          Invalid incoming core link.
        </Alert>
      </IosPage>
    );
  }

  return (
    <IosPage>
      <MobileTopBar
        title={row?.customer?.name || "Incoming core"}
        subtitle={[row?.shippingCarrier, row?.trackingNumber].filter(Boolean).join(" • ") || "Physical receive"}
        onBack={() => navigate("/m/warehouse/receiving?tab=cores")}
      />

      {detailQuery.isLoading ? <LinearProgress sx={{ mt: 1 }} /> : null}
      {notice ? (
        <Alert severity={notice.type === "success" ? "success" : "error"} sx={{ mt: 1 }}>
          {notice.text}
        </Alert>
      ) : null}

      <Alert severity="info" sx={{ mt: 1 }}>
        Photos and notes only. Credit is applied later on Core Returns.
      </Alert>

      <Stack spacing={1} sx={{ mt: 1 }}>
        <IosCard>
          <Stack spacing={0.6}>
            <Typography sx={{ fontWeight: 850, fontSize: 14 }}>Shipment</Typography>
            <Typography sx={{ fontSize: 13 }}>Customer: {row?.customer?.name || "—"}</Typography>
            <Typography sx={{ fontSize: 13 }}>
              Unit: {[row?.unit?.optionalTag, row?.unit?.serialNumber].filter(Boolean).join(" · ") || "—"}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>Tracking: {row?.trackingNumber || "—"}</Typography>
            <Typography sx={{ fontSize: 13 }}>
              Status: {alreadyReceived ? "Received (pending credit)" : "Awaiting receive"}
            </Typography>
            {alreadyReceived ? (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                {existingPhotos.length} photo(s) ·{" "}
                {row?.warehouseInspectedAt ? new Date(row.warehouseInspectedAt).toLocaleString() : ""}
              </Typography>
            ) : null}
          </Stack>
        </IosCard>

        <IosCard>
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 850, fontSize: 14 }}>Receive core</Typography>
            <TextField
              fullWidth
              label="Core part number"
              placeholder="Search CORE SKU or name"
              value={coreSearch}
              onChange={(e) => setCoreSearch(e.target.value)}
              disabled={receiveMut.isPending}
            />
            {receivedCoreItem ? (
              <Alert severity="success" sx={{ py: 0.5 }}>
                Selected: {[receivedCoreItem.sku, receivedCoreItem.name].filter(Boolean).join(" · ")}
              </Alert>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                Search and tap a CORE item below.
              </Typography>
            )}
            {coreOptions.length ? (
              <Stack spacing={0.75}>
                {coreOptions.map((item) => {
                  const docId = String(item?.documentId || "").trim();
                  if (!docId) return null;
                  const selected = receivedCoreItem?.documentId === docId;
                  return (
                    <Button
                      key={docId}
                      fullWidth
                      variant={selected ? "contained" : "outlined"}
                      onClick={() =>
                        setReceivedCoreItem({
                          documentId: docId,
                          sku: String(item?.sku || ""),
                          name: String(item?.name || ""),
                        })
                      }
                      sx={{
                        justifyContent: "flex-start",
                        textTransform: "none",
                        fontWeight: 700,
                        ...(selected
                          ? { bgcolor: "#147A5C", "&:hover": { bgcolor: "#0D6149" } }
                          : { color: "#8F3700", borderColor: "rgba(255,106,0,0.35)" }),
                      }}
                    >
                      {[item?.sku, item?.name].filter(Boolean).join(" · ") || docId}
                    </Button>
                  );
                })}
              </Stack>
            ) : null}
            <TextField
              fullWidth
              label="Receive notes"
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={receiveMut.isPending}
            />

            <Box>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 0.5 }}>Photos</Typography>
              <Box
                component="label"
                onDragEnter={(e) => {
                  e.preventDefault();
                  if (!receiveMut.isPending) setDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!receiveMut.isPending) setDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  if (!receiveMut.isPending) addFiles(e.dataTransfer.files);
                }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  border: "2px dashed",
                  borderColor: dragActive ? "#B54708" : "rgba(15,15,18,0.12)",
                  borderRadius: 2,
                  px: 2,
                  py: 2.5,
                  bgcolor: dragActive ? "rgba(255,106,0,0.06)" : "#F7F8FA",
                  cursor: receiveMut.isPending ? "not-allowed" : "pointer",
                  opacity: receiveMut.isPending ? 0.6 : 1,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F0F12" }}>
                  Tap to capture or choose photos
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#6E7278" }}>Drag and drop also works on desktop</Typography>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  capture="environment"
                  hidden
                  disabled={receiveMut.isPending}
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = "";
                  }}
                />
              </Box>
            </Box>

            {existingPhotos.length || pendingPreviews.length ? (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {existingPhotos.map((photo, index) =>
                  photo?.url ? (
                    <PhotoThumb
                      key={String(photo.id || photo.url || index)}
                      src={photo.url}
                      label={photo.name || `Saved photo ${index + 1}`}
                      onClick={() => setPreview({ url: photo.url, label: photo.name || `Saved photo ${index + 1}` })}
                    />
                  ) : null,
                )}
                {pendingPreviews.map((photo, index) => (
                  <PhotoThumb
                    key={photo.key}
                    src={photo.url}
                    label={photo.label}
                    onClick={() => setPreview({ url: photo.url, label: photo.label })}
                    onRemove={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                    removable={!receiveMut.isPending}
                  />
                ))}
              </Stack>
            ) : null}

            {files.length ? (
              <Typography sx={{ fontSize: 12.25, color: "text.secondary" }}>{files.length} new photo(s) selected</Typography>
            ) : null}

            {receiveMut.isPending ? <LinearProgress /> : null}
            <Button
              fullWidth
              variant="contained"
              disabled={
                receiveMut.isPending ||
                !receivedCoreItem?.documentId ||
                (!files.length && !alreadyReceived)
              }
              onClick={() => receiveMut.mutate()}
              sx={{
                bgcolor: "#147A5C",
                "&:hover": { bgcolor: "#0D6149" },
                textTransform: "none",
                fontWeight: 850,
              }}
            >
              {alreadyReceived ? "Update receive" : "Mark received"}
            </Button>
            {row?.sourceInvoiceDocumentId && row?.sourceSalesOrderItemDocumentId ? (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/m/core-returns")}
                sx={{ textTransform: "none", fontWeight: 800, color: "#8F3700", borderColor: "rgba(255,106,0,0.35)" }}
              >
                Open Core Returns
              </Button>
            ) : null}
          </Stack>
        </IosCard>
      </Stack>

      <Dialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="md">
        <DialogContent sx={{ p: 0, bgcolor: "#0F0F12" }}>
          {preview?.url ? (
            <Box
              component="img"
              src={preview.url}
              alt={preview.label || "Photo preview"}
              sx={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </IosPage>
  );
}
